import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { getRentPayments, getRentSummary, payRent, getTenantLeases, getAnnouncements, getDashboardStats } from '@/services/api';
import { 
  IndianRupee, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  Home, 
  User, 
  CreditCard,
  AlertCircle,
  Bell,
  AlertTriangle
} from 'lucide-react';

const TenantHome = () => {
  const { user } = useAuth();
  const [rentPayments, setRentPayments] = useState([]);
  const [rentSummary, setRentSummary] = useState(null);
  const [lease, setLease] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [dashStats, setDashStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [paymentsData, summaryData, leasesData, announcementsData, statsData] = await Promise.all([
        getRentPayments(),
        getRentSummary(),
        getTenantLeases(),
        getAnnouncements().catch(() => []),
        getDashboardStats().catch(() => ({}))
      ]);
      setRentPayments(paymentsData);
      setRentSummary(summaryData);
      if (leasesData && leasesData.length > 0) {
        setLease(leasesData[0]);
      }
      setAnnouncements(announcementsData.slice(0, 3));
      setDashStats(statsData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePayRent = async (paymentId) => {
    try {
      setPayingId(paymentId);
      await payRent(paymentId);
      toast.success('Rent paid successfully!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setPayingId(null);
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  // Calculate rent reminder
  const getRentReminder = () => {
    // 'pending' is legacy; current generation uses 'unpaid'
    const pendingPayments = rentPayments.filter(p => p.status === 'pending' || p.status === 'unpaid');
    if (pendingPayments.length === 0) return null;

    const currentDay = new Date().getDate();
    
    if (currentDay >= 5 && currentDay <= 10) {
      return { type: 'warning', title: 'Rent Due Soon!', message: `Please pay your rent to avoid late fees.` };
    } else if (currentDay > 10) {
      return { type: 'urgent', title: 'Rent Overdue!', message: `Your rent payment is overdue. Please pay immediately.` };
    }
    return null;
  };

  const rentReminder = getRentReminder();
  const pendingPayments = rentPayments.filter(p => p.status === 'pending' || p.status === 'unpaid');
  const paidPayments = rentPayments.filter(p => p.status === 'paid');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Welcome, {user?.name}</h1>
          <p className="text-slate-600 mt-1">Unit {user?.unit_number} | Tenant Dashboard</p>
        </div>
      </div>

      {/* Rent Reminder Alert */}
      {rentReminder && (
        <Alert className={`${rentReminder.type === 'urgent' ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
          <AlertTriangle className={`h-5 w-5 ${rentReminder.type === 'urgent' ? 'text-red-600' : 'text-amber-600'}`} />
          <AlertTitle className={rentReminder.type === 'urgent' ? 'text-red-800' : 'text-amber-800'}>
            {rentReminder.title}
          </AlertTitle>
          <AlertDescription className={rentReminder.type === 'urgent' ? 'text-red-700' : 'text-amber-700'}>
            {rentReminder.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={pendingPayments.length > 0 ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${pendingPayments.length > 0 ? 'text-red-600' : 'text-green-600'}`}>Pending Dues</p>
                <p className={`text-2xl font-bold flex items-center gap-1 ${pendingPayments.length > 0 ? 'text-red-700' : 'text-green-700'}`}>
                  <IndianRupee className="h-5 w-5" />
                  {rentSummary?.total_due?.toLocaleString('en-IN') || 0}
                </p>
              </div>
              {pendingPayments.length > 0 ? (
                <AlertCircle className="h-8 w-8 text-red-400" />
              ) : (
                <CheckCircle2 className="h-8 w-8 text-green-400" />
              )}
            </div>
            <p className={`text-xs mt-1 ${pendingPayments.length > 0 ? 'text-red-500' : 'text-green-500'}`}>
              {pendingPayments.length} payment(s) pending
            </p>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Total Paid</p>
                <p className="text-2xl font-bold text-green-700 flex items-center gap-1">
                  <IndianRupee className="h-5 w-5" />
                  {rentSummary?.total_paid?.toLocaleString('en-IN') || 0}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Monthly Rent</p>
                <p className="text-2xl font-bold text-blue-700 flex items-center gap-1">
                  <IndianRupee className="h-5 w-5" />
                  {lease?.monthly_rent?.toLocaleString('en-IN') || 0}
                </p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600">Lease Status</p>
                <p className="text-2xl font-bold text-purple-700 capitalize">{lease?.status || 'N/A'}</p>
              </div>
              <Home className="h-8 w-8 text-purple-400" />
            </div>
            <p className="text-xs text-purple-500 mt-1">Since {lease?.lease_start || 'N/A'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      {dashStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/tenant/bills'} data-testid="stat-pending-bills">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-red-50 rounded-xl flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Pending Bills</p>
                  <p className="text-xl font-bold text-slate-900">{dashStats.pending_bills || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/tenant/amenities'} data-testid="stat-upcoming-bookings">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Upcoming Bookings</p>
                  <p className="text-xl font-bold text-slate-900">{dashStats.upcoming_bookings || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/tenant/events'} data-testid="stat-registered-events">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-purple-50 rounded-xl flex items-center justify-center">
                  <Bell className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Registered Events</p>
                  <p className="text-xl font-bold text-slate-900">{dashStats.registered_events || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/tenant/complaints'} data-testid="stat-open-complaints">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-orange-50 rounded-xl flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Open Complaints</p>
                  <p className="text-xl font-bold text-slate-900">{dashStats.open_complaints || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Rent Payments - Main Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Pending Payments */}
          {pendingPayments.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader className="bg-amber-50 rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <Clock className="h-5 w-5" />
                  Pending Rent Payments
                </CardTitle>
                <CardDescription className="text-amber-600">
                  Pay your pending rent to avoid penalties
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-3">
                  {pendingPayments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200"
                      data-testid={`pending-payment-${payment.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-amber-100 rounded-lg">
                          <Clock className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{formatMonth(payment.month)}</p>
                          <p className="text-sm text-slate-500">Unit: {payment.unit_number} | To: {payment.landlord_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-slate-900 flex items-center gap-1">
                            <IndianRupee className="h-4 w-4" />
                            {payment.amount?.toLocaleString('en-IN')}
                          </p>
                        </div>
                        <Button
                          onClick={() => handlePayRent(payment.id)}
                          disabled={payingId === payment.id}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`pay-btn-${payment.id}`}
                        >
                          {payingId === payment.id ? 'Processing...' : 'Pay Now'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-secondary" />
                Payment History
              </CardTitle>
              <CardDescription>Your past rent payments</CardDescription>
            </CardHeader>
            <CardContent>
              {paidPayments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CreditCard className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                  <p>No payment history yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {paidPayments.slice(0, 5).map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{formatMonth(payment.month)}</p>
                          <p className="text-xs text-slate-500">Paid: {payment.paid_date}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 flex items-center gap-1">
                          <IndianRupee className="h-3 w-3" />
                          {payment.amount?.toLocaleString('en-IN')}
                        </span>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Paid</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Lease Details */}
          {lease && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Home className="h-5 w-5 text-secondary" />
                  Lease Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between p-2 bg-slate-50 rounded">
                  <span className="text-slate-600 text-sm">Landlord</span>
                  <span className="font-medium text-sm">{lease.landlord_name}</span>
                </div>
                <div className="flex justify-between p-2 bg-slate-50 rounded">
                  <span className="text-slate-600 text-sm">Unit</span>
                  <span className="font-medium text-sm">{lease.unit_number}</span>
                </div>
                <div className="flex justify-between p-2 bg-slate-50 rounded">
                  <span className="text-slate-600 text-sm">Monthly Rent</span>
                  <span className="font-medium text-sm text-secondary">₹{lease.monthly_rent?.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between p-2 bg-slate-50 rounded">
                  <span className="text-slate-600 text-sm">Start Date</span>
                  <span className="font-medium text-sm">{lease.lease_start}</span>
                </div>
                <div className="flex justify-between p-2 bg-slate-50 rounded">
                  <span className="text-slate-600 text-sm">Status</span>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">{lease.status}</Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Announcements */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Bell className="h-5 w-5 text-secondary" />
                Announcements
                {announcements.length > 0 && (
                  <Badge variant="secondary" className="ml-auto">{announcements.length}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {announcements.length === 0 ? (
                <div className="text-center py-4 text-slate-500">
                  <Bell className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No announcements</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {announcements.map((a) => (
                    <div key={a.id} className={`p-3 rounded-lg border ${
                      a.priority === 'urgent' ? 'border-red-200 bg-red-50' : 
                      a.priority === 'important' ? 'border-amber-200 bg-amber-50' : 
                      'border-slate-200 bg-slate-50'
                    }`}>
                      <p className="font-medium text-sm text-slate-900">{a.title}</p>
                      <p className="text-xs text-slate-600 mt-1 line-clamp-2">{a.content}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(a.created_at).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tenant Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5 text-secondary" />
                Your Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Name</span>
                <span className="font-medium">{user?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Email</span>
                <span className="font-medium text-xs">{user?.email}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Tenant ID</span>
                <code className="text-xs bg-slate-100 px-2 py-1 rounded">{user?.id?.slice(0, 8)}</code>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TenantHome;
